import { parseISO } from 'date-fns';
import { apply, dropLastWhile, dropWhile, equals } from 'ramda';
import { getHourMin, getMSec, getSec, getYearMonthDay } from './dateUtils';
import { stringCompareNumeric } from './mergeSort';

export const capitalize = (str: string): string => {
  return str
    .split(' ')
    .map(
      w =>
        (w.length > 0 ? w[0].toLocaleUpperCase() : '') +
        (w.length > 1 ? w.slice(1).toLowerCase() : '')
    )
    .join(' ');
};

export const firstLetterLowerCase = (str: string): string => {
  return str.charAt(0).toLowerCase() + str.slice(1);
};

export const padToLength = (
  toPad: string | number,
  length: number,
  paddingCharacter: string
) => {
  const stringToPad = toPad.toString();
  const padding = paddingCharacter.repeat(length);
  return (padding + stringToPad).slice(stringToPad.length);
};

export const splitPermutationArray = (
  value: string,
  splitString: string = '.'
): string[] => {
  const split = value.split(splitString);
  return split.reduce<string[]>((a, b, index) => {
    a.push(split.slice(0, index + 1).join('.'));
    return a;
  }, []);
};
/**
 * Tar en lista med strängar och kommaseparerar alla element förutom det sista som separeras med "och"
 * @param list
 */
export const listToString = (list: string[]): string => {
  if (list.length < 3) {
    return list.join(' och ');
  } else {
    return `${list.slice(0, list.length - 1).join(', ')} och ${
      list[list.length - 1]
    }`;
  }
};

export const stringEqualIgnoreCase = (s1: string, s2: string): boolean =>
  stringCompareNumeric(s1, s2) === 0;

export function strDropLastWhile(
  fn: (a: string) => boolean,
  list: string
): string;
export function strDropLastWhile(
  fn: (a: string) => boolean
): (list: string) => string;
export function strDropLastWhile(...args: any): any {
  return apply(dropLastWhile, args);
}

export function strDropWhile(fn: (a: string) => boolean, list: string): string;
export function strDropWhile(
  fn: (a: string) => boolean
): (list: string) => string;
export function strDropWhile(...args: any): any {
  return apply(dropWhile, args);
}

export const pathJoin = (parts: string[], sep?: string): string => {
  const separator = sep ?? '/';
  const partsWithoutSeparator = parts.map(part =>
    strDropWhile(equals(separator), strDropLastWhile(equals(separator), part))
  );
  return partsWithoutSeparator.join(separator);
};

const intRegex: RegExp = /^-?[0-9]+$/;

/**
 * Tries to convert a string to an integer
 * @param {string} str - The string.
 * @returns {number} The date if conversion is successful, NaN otherwise.
 */
export const strToInt = (str: string): number => {
  if (intRegex.test(str)) {
    return parseInt(str);
  } else {
    return NaN;
  }
};

const dateTimeRegex: RegExp =
  /^(\d{4}-\d{2}-\d{2})(?:\s\d{2}:\d{2}(?::\d{2}(?:\.\d{3})?)?)?$/;

/**
 * Tries to convert a string to a datetime
 * @param {string} str - The string.
 * @returns {Date} The datetime if conversion is successful, undefined otherwise.
 */

export const tryStrToDateTime = (str: string) => {
  if (dateTimeRegex.test(str)) {
    return parseISO(str);
  } else {
    return undefined;
  }
};

/**
 * Converts a datetime to a string. Hours and minutes are always returned, seconds and milliseconds are only returned if they are non-zero and outputSeconds is true.
 * @param {Date} dateTime - The datetime.
 * @param {boolean} outputSeconds - Returns nonzero seconds and milliseconds (dewfault false).
 * @param {string} dateSeparator - The date separator to use (default -).
 * @param {string} timeSeparator - The time separator to use (default :).
 * @param {string} millisecondSeparator - The millisecond separator to use (default .).
 * @param {string} dateTimeSeparator - The time separator to use (default space).
 * @returns {string} The datetime as a string.
 */

export const dateTimeToStr = (
  dateTime: Date,
  outputSeconds: boolean = false,
  dateSeparator: string = '-',
  timeSeparator: string = ':',
  millisecondSeparator: string = '.',
  dateTimeSeparator: string = ' '
) => {
  return (
    dateToStr(dateTime, dateSeparator) +
    dateTimeSeparator +
    timeToStr(dateTime, outputSeconds, timeSeparator, millisecondSeparator)
  );
};

const dateRegex: RegExp = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Tries to convert a string to a date
 * @param {string} str - The string.
 * @returns {Date} The datetime if conversion is successful, undefined otherwise.
 */

export const tryStrToDate = (str: string) => {
  if (dateRegex.test(str)) {
    return parseISO(str);
  } else {
    return undefined;
  }
};

/**
 * Converts a date to a string
 * @param {Date} date - The date.
 * @param {string} separator - The separator to use (default -).
 * @returns {string} The date as a string.
 */

export const dateToStr = (date: Date, separator: string = '-') => {
  const [y, m, d] = getYearMonthDay(date);
  return (
    y.toString().padStart(4, '0') +
    separator +
    m.toString().padStart(2, '0') +
    separator +
    d.toString().padStart(2, '0')
  );
};

const timeRegex: RegExp = /^\d{2}:\d{2}(?::\d{2}(?:\.\d{3})?)?$/;

/**
 * Tries to convert a string to a time
 * @param {string} str - The string.
 * @returns {Date} The time if conversion is successful, undefined otherwise.
 */

export const tryStrToTime = (str: string) => {
  if (timeRegex.test(str)) {
    return parseISO(`1970-01-01 ${str}`);
  } else {
    return undefined;
  }
};

/**
 * Converts a time to a string. Hours and minutes are always returned, seconds and milliseconds are only returned if they are non-zero and outputSeconds is true.
 * @param {Date} time - The time.
 * @param {boolean} outputSeconds - Returns nonzero seconds and milliseconds (dewfault false).
 * @param {string} timeSeparator - The time separator to use (default :).
 * @param {string} millisecondSeparator - The millisecond separator to use (default .).
 * @returns {string} The time as a string.
 */

export const timeToStr = (
  time: Date,
  outputSeconds: boolean = false,
  timeSeparator: string = ':',
  millisecondSeparator: string = '.'
) => {
  const [h, n] = getHourMin(time);
  const s = outputSeconds ? getSec(time) : 0;
  const ms = outputSeconds ? getMSec(time) : 0;
  return (
    h.toString().padStart(2, '0') +
    timeSeparator +
    n.toString().padStart(2, '0') +
    (s > 0
      ? timeSeparator +
        s.toString().padStart(2, '0') +
        (ms > 0 ? millisecondSeparator + ms.toString().padStart(3, '0') : '')
      : '')
  );
};

export const strToBase64utf8 = (str: string) => {
  const utf8Data = new TextEncoder().encode(str);
  let binaryString = '';
  for (let i = 0; i < utf8Data.length; i++) {
    binaryString += String.fromCharCode(utf8Data[i]);
  }
  return btoa(binaryString);
};

export const singleSignonURL = (
  db: string,
  sign: string,
  key: string,
  routine: string,
  id: string
) => {
  return (
    'aveny:' +
    [
      strToBase64utf8(db),
      strToBase64utf8(sign),
      key,
      strToBase64utf8(routine),
      id,
    ].join('/')
  );
};
